import {memo, useEffect, useState} from "react";
import {useRouter} from "next/router";
import Login from "@FRAMEWORK/utils/login";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {GlobalStyle as WebGlobalStyle} from "@yb/ui/desktop/global";
import {GlobalStyle as MobileGlobalStyle} from "@yb/ui/mobile/global";
import icon from "@PUBLIC/favicon/ico_yb.ico";
import Head from "next/head";
import {device} from "@yb/utils";
import useOverlay from "@/hooks/common/useOverlay";
import MobileAlert from "@COMPONENTS/common/modal/alert/Mobile";
import DesktopAlert from "@COMPONENTS/common/modal/alert/Desktop";
import WebIcoMove from "@FRAMEWORK/ui/desktop/layout/IcoMove";
import MobileIcoMove from "@FRAMEWORK/ui/mobile/layout/IcoMove";
import Floating from "@COMPONENTS/layout/floating";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const MessageUtil = () => {
	const [alert] = useOverlay();
	const [confirm] = useOverlay();
	const type = device.current();
	const message = (msg: string, callback?: () => void) => {
		if (type === "Desktop") {
			return new Promise<boolean>(resolve =>
				alert(prop => (
					<DesktopAlert
						{...prop}
						resolve={resolve}
						text={msg}
						afterClose={callback || null}
						afterConfirm={callback || null}
					/>
				)),
			);
		} else {
			return new Promise<boolean>(resolve =>
				alert(prop => (
					<MobileAlert
						{...prop}
						resolve={resolve}
						text={msg}
						afterClose={callback || null}
						afterConfirm={callback || null}
					/>
				)),
			);
		}
	};
	const messageConfirm = (msg: string, callback?: () => void) => {
		if (type === "Desktop") {
			return new Promise<boolean>(resolve =>
				confirm(prop => (
					<DesktopAlert {...prop} resolve={resolve} alertType="confirm" text={msg} afterConfirm={callback || null} />
				)),
			);
		} else {
			return new Promise<boolean>(resolve =>
				confirm(prop => (
					<MobileAlert {...prop} resolve={resolve} alertType="confirm" text={msg} afterConfirm={callback || null} />
				)),
			);
		}
	};
	window.MessageUtil = {
		alert: message,
		confirm: messageConfirm,
	};
	return <></>;
};

const LoginUtil = () => {
	window.LoginUtil = new Login(publicRuntimeConfig);
	return <></>;
};

const PopupUtil = () => {
	const [open] = useOverlay();
	const promise = async (PopupComponent, props) =>
		new Promise<boolean>(resolve =>
			open(overlay => (
				<PopupComponent
					{...overlay}
					close={() => {
						typeof resolve === "function" && resolve(false);
						overlay.close();
					}}
					{...props}
				/>
			)),
		);
	window.PopupUtil = {
		promise,
	};
	return <></>;
};

const Function = memo(() => (
	<>
		<MessageUtil />
		<LoginUtil />
		<PopupUtil />
	</>
));

const Container = ({children}) => {
	const router = useRouter();
	const [indicator, setIndicator] = useState(false);
	const deviceType = device.current();
	useEffect(() => {
		const handleRouteStart = () => setIndicator(true);
		const handleRouteComplete = () => setIndicator(false);

		router.events.on("routeChangeStart", handleRouteStart);
		router.events.on("routeChangeComplete", handleRouteComplete);
	}, []);

	return (
		<>
			{device.current() === "Mobile" ? (
				<>
					<Head>
						<meta name="mobile-web-app-capable" content="yes" />
						<meta name="apple-mobile-web-app-capable" content="yes" />
						<meta name="format-detection" content="telephone=no" />
						<meta name="viewport" content="user-scalable=yes, initial-scale=1, maximum-scale=1, width=device-width" />
						<link rel="apple-touch-icon-precomposed" href="/static/images/common/ico_home.png" />
						<link rel="shortcut icon" type="image/x-icon" href={icon} />
					</Head>
					<MobileGlobalStyle />
				</>
			) : (
				<>
					<Head>
						<meta httpEquiv="X-UA-Compatible" content="IE=edge" />
						<link rel="shortcut icon" type="image/x-icon" href={icon} />
					</Head>
					<WebGlobalStyle />
				</>
			)}
			<div>
				{children}
				<Function />
				<Floating />
				{deviceType === "Desktop" && indicator && <WebIcoMove />}
				{deviceType === "Mobile" && indicator && <MobileIcoMove />}
			</div>
		</>
	);
};

export default Container;
